import type {
  CodeableConcept,
  Coding,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  VIEW_ACCESS_TAG_SYSTEM,
  VIEW_IDENTIFIER_SYSTEM,
  VIEW_STATUS_TAG_SYSTEM,
} from '#src/codeSystems.ts'
import { type DateString, today } from '#src/dates.ts'
import { Tag } from '#src/decorators.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedBasic } from '#src/models/basic.ts'
import { isOfType, parseReferenceString } from '#src/utils.ts'

export type ViewAccess = Reference<Practitioner> | 'shared'

export abstract class View extends ExtendedBasic {
  constructor({
    code,
    name,
    owner,
    access,
  }: {
    code: CodeableConcept
    name: string
    owner: Reference<Practitioner>
    access?: ViewAccess
  }) {
    super({
      code,
      created: today(), // Basic requires a date for created instead of a datetime.
      author: owner,
    })
    this.name = name
    this._status = View.status.active
    if (access === 'shared') {
      this._access = {
        code: access,
        system: VIEW_ACCESS_TAG_SYSTEM,
      }
    } else if (access) {
      this._access = {
        code: access.reference,
        system: VIEW_ACCESS_TAG_SYSTEM,
      }
    } else {
      this._access = {
        code: owner.reference,
        system: VIEW_ACCESS_TAG_SYSTEM,
      }
    }
  }

  get name(): string {
    const name = this.resource.identifier?.[0]?.value
    invariant(name, 'Missing name')
    return name
  }

  set name(value: string) {
    this.resource.identifier = [{ system: VIEW_IDENTIFIER_SYSTEM, value }]
  }

  static status = {
    active: {
      system: VIEW_STATUS_TAG_SYSTEM,
      code: 'active',
    } satisfies Coding,
    enteredInError: {
      system: VIEW_STATUS_TAG_SYSTEM,
      code: 'entered-in-error',
    } satisfies Coding,
  }

  @Tag({
    system: VIEW_STATUS_TAG_SYSTEM,
  })
  _status: Coding

  delete(): void {
    this._status = View.status.enteredInError
  }

  get isDeleted(): boolean {
    return this._status.code === View.status.enteredInError.code
  }

  @Tag({
    system: VIEW_ACCESS_TAG_SYSTEM,
  })
  _access: Coding

  get access(): ViewAccess {
    const code = this._access?.code
    if (code === 'shared' || !code) {
      return 'shared'
    }
    const reference = parseReferenceString(code)
    invariant(isOfType(reference, 'Practitioner'), 'Invalid access reference')
    return reference
  }

  set access(value: ViewAccess) {
    if (value === 'shared') {
      this._access = {
        code: 'shared',
        system: VIEW_ACCESS_TAG_SYSTEM,
      }
      return
    }
    const referenceString = value.reference
    invariant(referenceString, 'Invalid access reference')
    this._access = {
      code: referenceString,
      system: VIEW_ACCESS_TAG_SYSTEM,
    }
  }

  get created(): DateString {
    const created = this.resource.created
    invariant(created, 'Missing created')
    return created
  }
}
